import { Injectable } from '@angular/core';
import {
  collectBrowserFingerprintPayload,
  DEVICE_FINGERPRINT_CACHE_MS,
  sessionDeviceHeaders,
} from '@ewentscompany/ewents-settings';

/** Browser fingerprint payload for session headers. IP is bound server-side via Cloudflare. */
export interface DeviceInfo {
  latitude?: number;
  longitude?: number;
  timezone?: string;
  language?: string;
  screenResolution?: string;
  platform?: string;
  userAgent?: string;
  colorDepth?: string;
  pixelRatio?: string;
  hardwareConcurrency?: string;
  maxTouchPoints?: string;
  deviceMemory?: string;
  canvas?: string;
  webgl?: string;
  audio?: string;
  timestamp?: number;
}

const CACHE_KEY = 'device_info_cache_v4';
/** Pre-v4 cache keys — dropped when fingerprint hashing / IP client fetch changed. */
const LEGACY_CACHE_KEYS = ['device_info_cache_v3', 'device_info_cache'] as const;
/** Align with remember-me inactivity ceiling — avoid re-sampling canvas/webgl mid-session. */
const CACHE_DURATION = DEVICE_FINGERPRINT_CACHE_MS;

@Injectable({
  providedIn: 'root',
})
export class DeviceInfoService {
  private cache: DeviceInfo | null = null;
  private cacheTimestamp = 0;
  private updatePromise: Promise<DeviceInfo> | null = null;

  /** Headers for cookie session checks. Reuses the cached fingerprint. */
  async getSessionHeaders(): Promise<Record<string, string>> {
    return sessionDeviceHeaders(await this.getDeviceInfo());
  }

  async getDeviceInfo(): Promise<DeviceInfo> {
    const now = Date.now();
    if (this.cache && now - this.cacheTimestamp < CACHE_DURATION) {
      return this.cache;
    }
    // The interceptor can run before app initializers (and CRM has no eager
    // initializer). Reuse the persisted fingerprint before sampling a new one.
    if (!this.cache && this.loadFromCache()) return this.cache!;
    if (this.updatePromise) return this.updatePromise;

    this.updatePromise = this.updateDeviceInfo();
    try {
      return await this.updatePromise;
    } finally {
      this.updatePromise = null;
    }
  }

  private async updateDeviceInfo(): Promise<DeviceInfo> {
    const fp = await collectBrowserFingerprintPayload();
    const info: DeviceInfo = {
      timestamp: Date.now(),
      timezone: fp.timezone,
      language: fp.language,
      screenResolution: fp.screen,
      platform: fp.platform,
      userAgent: fp.userAgent,
      colorDepth: fp.colorDepth,
      pixelRatio: fp.pixelRatio,
      hardwareConcurrency: fp.hardwareConcurrency,
      maxTouchPoints: fp.maxTouchPoints,
      deviceMemory: fp.deviceMemory,
      canvas: fp.canvas,
      webgl: fp.webgl,
      audio: fp.audio,
    };

    this.cache = info;
    this.cacheTimestamp = Date.now();
    this.persist(info, this.cacheTimestamp);

    // Optional geo for session list display only — never block auth headers / HTTP.
    void this.getGeolocation()
      .then(position => {
        if (!position || !this.cache) return;
        this.cache = {
          ...this.cache,
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
        };
        this.persist(this.cache, this.cacheTimestamp);
      })
      .catch(() => {});

    return info;
  }

  private persist(info: DeviceInfo, timestamp: number): void {
    try {
      localStorage.setItem(CACHE_KEY, JSON.stringify({ info, timestamp }));
    } catch {
      /* ignored */
    }
  }

  private getGeolocation(): Promise<GeolocationPosition> {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error('Geolocation not supported'));
        return;
      }
      const timeout = setTimeout(() => reject(new Error('Geolocation timeout')), 4000);
      navigator.geolocation.getCurrentPosition(
        position => {
          clearTimeout(timeout);
          resolve(position);
        },
        error => {
          clearTimeout(timeout);
          reject(error);
        },
        { enableHighAccuracy: false, timeout: 4000, maximumAge: 600_000 }
      );
    });
  }

  loadFromCache(): DeviceInfo | null {
    try {
      const cached = localStorage.getItem(CACHE_KEY);
      if (!cached) return null;
      const { info, timestamp } = JSON.parse(cached);
      if (Date.now() - timestamp < CACHE_DURATION) {
        this.cache = info;
        this.cacheTimestamp = timestamp;
        return info;
      }
    } catch {
      /* ignore */
    }
    return null;
  }

  initialize(): void {
    this.dropLegacyCache();
    this.loadFromCache();
    void this.getDeviceInfo();
  }

  clearCache(): void {
    this.cache = null;
    this.cacheTimestamp = 0;
    try {
      localStorage.removeItem(CACHE_KEY);
      for (const key of LEGACY_CACHE_KEYS) {
        localStorage.removeItem(key);
      }
    } catch {
      /* ignore */
    }
  }

  private dropLegacyCache(): void {
    try {
      for (const key of LEGACY_CACHE_KEYS) {
        localStorage.removeItem(key);
      }
    } catch {
      /* ignore */
    }
  }
}
